import { Outlet, Route, Routes } from "react-router-dom";
import React, { useEffect, useRef } from "react";
import { UserInput } from "@webuild-ai/admin/models/components";
import {
  createDefaultBrandsConfig,
  getDashboardUrlSegments,
  useAuth,
  useBrand,
  useUser,
  verifyUserPermissions,
} from "@webuild-ai/react-auth";

import { VERSION_INFO } from "@/types/version";
import { logger } from "@/telemetry";

import { useWebuildAPIClient } from "./providers/WeBuildClientProvider";
import LoginPrompt from "./components/LoginPrompt/LoginPrompt";
import IndexPage from "./pages/index";
import { WorkflowIndexPage } from "./pages/workflows/WorkflowIndexPage";
import { WorkflowEditPage } from "./pages/workflows/WorkflowEditPage";
import { WorkflowPage } from "./pages/workflows/WorkflowPage";
import { WorkflowDatasetsPage } from "./pages/workflows/WorkflowDatasetsPage";
import { RunPage } from "./pages/workflows/RunPage";
import { WorkflowPlaygroundPage } from "./pages/workflows/WorkflowPlaygroundPage";
import LoadingPage from "./pages/Loading";
import DefaultLayout from "./layouts/default";
import DataHubIndexPage from "./pages/datahub/DataHubIndexPage";
import { DataHubFilesPage } from "./pages/datahub/DataHubFilesPage";
import { SettingsPage } from "./pages/settings";
import { IntegrationCallback } from "./pages/settings/IntegrationCallback";
import { PromptsPage } from "./pages/prompts";
import { MetricsPage } from "./pages/MetricsPage";
import { ModelsPage } from "./pages/ModelsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { WorkflowMetricsPage } from "./pages/workflows/WorkflowMetricsPage";
import { DataHubMCPServerPage } from "./pages/datahub/DataHubMCPServerPage";
import DataHubGoogleDrive from "./pages/datahub/DataHubGoogleDrive";
import { useResponsive } from "./hooks/use-responsive";
import { useGlobalStore } from "./stores/GlobalStore";
import { TamperDetection } from "./components/Security/TamperDetection";
import { ProtectedRoute } from "./components/Security/ProtectedRoute";
import { AccessDeniedPage } from "./pages/AccessDeniedPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { AdminPage } from "./pages/admin";

// Create brands config at module level for getDashboardUrlSegments
const brandsConfig = createDefaultBrandsConfig();

const PrivateRoute: React.FC = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { adminClient } = useWebuildAPIClient();
  const { setBrand, brand } = useBrand();
  const { setUserProfile, userProfile } = useUser();
  const previousUserProfileRef = useRef(userProfile);
  const { sidebar } = useGlobalStore();
  const { setIsSidebarCompact } = sidebar;
  const { isMobile } = useResponsive();

  // Load permissions directly here where we have both user and adminClient
  const [permissionsLoaded, setPermissionsLoaded] = React.useState(false);
  const permissionsLoadedRef = React.useRef(false);

  // Load permissions ONLY AFTER userProfile is created
  React.useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const loadPermissions = async () => {
      // Wait for userProfile to be created first
      if (
        !user?.id ||
        !adminClient ||
        !isAuthenticated ||
        !userProfile ||
        permissionsLoadedRef.current
      ) {
        return;
      }

      try {
        const response = await verifyUserPermissions(adminClient, user.id);

        // Check if component is still mounted and request wasn't aborted
        if (abortController.signal.aborted || !isMounted) {
          return;
        }

        if (response && response.permissions) {
          const perms = {
            workflows: response.permissions.workflows || false,
            knowledge_hub: response.permissions.knowledge_hub || false,
            prompt_library: response.permissions.prompt_library || false,
            monitoring: response.permissions.monitoring || false,
            settings: response.permissions.settings || false,
          };

          permissionsLoadedRef.current = true;
          setPermissionsLoaded(true);

          // Store permissions with timestamp for integrity checking
          sessionStorage.setItem(
            "_perm_check",
            JSON.stringify({
              perms,
              timestamp: Date.now(),
              userId: user.id,
            }),
          );

          // Update the auth provider's permissions
          window.dispatchEvent(new CustomEvent("app:permissions-loaded", { detail: perms }));
        }
      } catch {
        // Only update state if component is still mounted
        if (!abortController.signal.aborted && isMounted) {
          permissionsLoadedRef.current = true;
          setPermissionsLoaded(true); // Mark as loaded even on error
        }
      }
    };

    loadPermissions();

    // Cleanup function
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [user?.id, adminClient, isAuthenticated, userProfile]);

  // Reset permissions on logout
  React.useEffect(() => {
    if (!isAuthenticated) {
      permissionsLoadedRef.current = false;
      setPermissionsLoaded(false);
      sessionStorage.removeItem("_perm_check");
    }
  }, [isAuthenticated]);

  // Effect to handle the sidebar compact state
  useEffect(() => {
    if (isMobile) setIsSidebarCompact(true);
    else setIsSidebarCompact(false);
  }, [isMobile, setIsSidebarCompact]);

  // Effect to set data-brand attribute on document root for brand-specific CSS
  useEffect(() => {
    if (brand?.prefix) {
      document.documentElement.dataset.brand = brand.prefix;
    }

    return () => {
      delete document.documentElement.dataset.brand;
    };
  }, [brand?.prefix]);

  // Effect hook to handle the async operation
  useEffect(() => {
    const fetchUserData = async () => {
      if (
        user &&
        user.email &&
        adminClient &&
        user.id &&
        user.name &&
        !userProfile // Only fetch if we don't already have a userProfile
      ) {
        try {
          // Removed token expiration check (user.exp) as UserProfile does not have 'exp'
          let tenant = user.email.split("@")[1];

          // Map bi-invest.com to investindustrial.com for multi-tenant support
          if (tenant === "bi-invest.com") {
            tenant = "investindustrial.com";
          }
          const userProfile: UserInput = {
            id: String(user.id),
            tenant: tenant,
            email: user.email,
            name: String(user.name),
            picture: String(user.picture || ""),
          };
          const userData = await adminClient.users.create(userProfile);

          if (!userData.id || !userData.tenant) {
            throw new Error("Received user data is missing required id or tenant");
          }

          // Don't override brand if it's already set for dev purposes
          // This allows WeBuild Azure auth to be used while preserving tenant-specific branding
          if (tenant !== "webuildailimited.onmicrosoft.com") {
            setBrand(tenant);
          } else if (!brand.name.includes("Invest") && !brand.name.includes("National Grid")) {
            // Only set WeBuild brand if not already set to II or NGV for dev purposes
            setBrand(tenant);
          }
          setUserProfile({
            ...userProfile,
            ...userData,
            id: userData.id ?? userProfile.id,
            name: String(userData.name ?? userProfile.name),
            picture: String(userData.picture ?? userProfile.picture),
          } as any);

          return userData;
        } catch (err) {
          logger.error("🚨 Error fetching user data", err instanceof Error ? err : undefined, {
            errorMessage: (err as any)?.message,
            responseStatus: (err as any)?.response?.status,
            adminClientURL: String(adminClient?._baseURL ?? ""),
          });
          // Only logout if this is not a validation error - let user stay logged in for now
          if (!(err as any)?.message?.includes("Response validation failed")) {
            logout();
          }
        }
      }
    };

    if (
      !isLoading &&
      isAuthenticated &&
      user &&
      user.email &&
      user.id &&
      user.name &&
      !userProfile
    ) {
      fetchUserData();
    }
  }, [
    isLoading,
    isAuthenticated,
    user,
    adminClient,
    setBrand,
    setUserProfile,
    userProfile,
    logout,
    brand.name,
  ]);

  // Effect to handle user profile updates via API
  useEffect(() => {
    const updateUserProfileAPI = async () => {
      if (adminClient && userProfile && previousUserProfileRef.current) {
        logger.info("update user profile");
        // Check if userProfile has changed (excluding system fields)
        const {
          id: _cId,
          name: _cName,
          tenant: _cTenant,
          email: _cEmail,
          picture: _cPicture,
          ...currentProfile
        } = userProfile;
        const {
          id: _pId,
          name: _pName,
          tenant: _pTenant,
          email: _pEmail,
          picture: _pPicture,
          ...previousProfile
        } = previousUserProfileRef.current;

        // Check if any user-modifiable fields have changed
        const hasChanges = JSON.stringify(currentProfile) !== JSON.stringify(previousProfile);

        logger.info("hasChanges", { hasChanges: String(hasChanges) });
        if (hasChanges) {
          try {
            await adminClient.users.updateUser({
              userId: userProfile.id,
              userInput: userProfile,
            });
          } catch (error) {
            logger.error(
              "Failed to update user profile",
              error instanceof Error ? error : undefined,
            );
            // Could add retry logic or user notification here
          }
        }
      }
      previousUserProfileRef.current = userProfile;
    };

    updateUserProfileAPI();
  }, [userProfile, adminClient]);

  // Show loading page until:
  // 1. Auth is complete (not isLoading)
  // 2. User profile is loaded
  // 3. Permissions are loaded
  if (isLoading || (isAuthenticated && (!userProfile || !permissionsLoaded))) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <LoginPrompt />;
  }

  return (
    <DefaultLayout brandName={brand.name}>
      {/* Only run tamper detection after permissions are loaded */}
      {permissionsLoaded && <TamperDetection />}
      <Outlet />
    </DefaultLayout>
  );
};

export default function App() {
  useEffect(() => {
    // Log version information on app load
    logger.info(`📦 Pathway v${VERSION_INFO.version}`, {
      commit: VERSION_INFO.commit,
      branch: VERSION_INFO.branch,
      buildDate: VERSION_INFO.buildDate,
    });

    function setAppHeight() {
      document.documentElement.style.setProperty("--app-height", `${window.innerHeight}px`);
    }
    window.addEventListener("resize", setAppHeight);

    setAppHeight();

    return () => window.removeEventListener("resize", setAppHeight);
  }, []);

  return (
    <Routes>
      {/* OAuth callback outside PrivateRoute - popup doesn't need full auth context */}
      <Route element={<IntegrationCallback />} path="/settings/integrations/callback" />
      <Route element={<PrivateRoute />} path="/">
        <Route index element={<IndexPage />} />
        <Route path="/chat">
          <Route index element={<WorkflowPage />} />
          <Route element={<WorkflowPage />} path=":chatId" />
        </Route>
        <Route element={<AccessDeniedPage />} path="/access-denied" />
        <Route path="/workflows">
          <Route
            index
            element={
              <ProtectedRoute requiredPermission="workflows">
                <WorkflowIndexPage />
              </ProtectedRoute>
            }
          />
          <Route path="playground">
            <Route
              index
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowPlaygroundPage />
                </ProtectedRoute>
              }
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowPlaygroundPage />
                </ProtectedRoute>
              }
              path=":workflowId"
            />
          </Route>
          <Route path=":workflowId">
            <Route
              index
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowPage />
                </ProtectedRoute>
              }
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowPage />
                </ProtectedRoute>
              }
              path=":chatId"
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowEditPage />
                </ProtectedRoute>
              }
              path="edit"
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowDatasetsPage />
                </ProtectedRoute>
              }
              path="datasets"
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowMetricsPage />
                </ProtectedRoute>
              }
              path="metrics"
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <RunPage />
                </ProtectedRoute>
              }
              path=":runId"
            />
          </Route>
        </Route>
        <Route path="/metrics">
          <Route
            index
            element={
              <ProtectedRoute requiredPermission="monitoring">
                <MetricsPage />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="/settings">
          <Route
            index
            element={
              <ProtectedRoute requiredPermission="settings">
                <SettingsPage />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="/admin">
          <Route
            index
            element={
              <ProtectedRoute requiredPermission="settings">
                <AdminPage />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="/datahub">
          <Route
            index
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubIndexPage />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubFilesPage />
              </ProtectedRoute>
            }
            path="files"
          />
          <Route
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubMCPServerPage />
              </ProtectedRoute>
            }
            path="mcp"
          />
          <Route
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubGoogleDrive />
              </ProtectedRoute>
            }
            path="google-drive"
          />
        </Route>
        {/* <Route path="tools">
          <Route index element={<ToolsIndexPage />} />
          <Route path="edit" element={<ToolsEditPage />} />
        </Route> */}
        <Route
          element={
            <ProtectedRoute requiredPermission="prompt_library">
              <PromptsPage />
            </ProtectedRoute>
          }
          path="prompts"
        />
        {/* Models Overview - always available (no permission required) */}
        <Route element={<ModelsPage />} path="/models" />
        {/* Dashboard routes - dynamically generated from brand configurations */}
        {/* Each brand can define a custom urlSegment in BrandProvider.tsx */}
        {getDashboardUrlSegments(brandsConfig).map((segment) => (
          <Route key={segment} element={<DashboardPage />} path={`/${segment}/*`} />
        ))}
        {/* Catch-all route for 404 */}
        <Route element={<NotFoundPage />} path="*" />
      </Route>
    </Routes>
  );
}
